import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import useI18n from 'hooks/useI18n'
import { ReadCompetitiveIntelligenceResearch } from 'services/competitive-intelligence'
import { trackEvent, trackEventOnce } from 'services/analytics'
import { apiRequest, exposeStrapiError } from 'utils'

const LANGUAGE_LOCALES = {
    Chinese: 'zh-CN', English: 'en-US', German: 'de-DE', Hindi: 'hi-IN',
    Japanese: 'ja-JP', Portuguese: 'pt-BR', Spanish: 'es-ES',
}

const errorStatus = result => Number(result?.error?.status || 0)

export default function useController() {
    const { language, t } = useI18n()
    const history = useHistory()
    const location = useLocation()
    const requestSequence = useRef(0)
    const [state, setState] = useState({
        status: 'loading', items: [], pagination: { total: 0, hasMore: false }, facets: { sources: [], statuses: [], competitors: [], conditions: [] }, loadingMore: false,
    })

    const filters = useMemo(() => {
        const params = new URLSearchParams(location.search)
        return {
            q: params.get('q') || '',
            source: params.get('source') || '',
            competitor: params.get('competitor') || '',
            condition: params.get('condition') || '',
            period: params.get('period') || '',
            status: params.get('status') || '',
            dateFrom: params.get('dateFrom') || '',
            dateTo: params.get('dateTo') || '',
            sort: params.get('sort') || 'newest',
        }
    }, [location.search])
    const [query, setQuery] = useState(filters.q)

    useEffect(() => setQuery(filters.q), [filters.q])

    const updateUrl = useCallback((name, value, replace = false) => {
        const params = new URLSearchParams(location.search)
        if (value && !(name === 'sort' && value === 'newest')) params.set(name, value)
        else params.delete(name)
        history[replace ? 'replace' : 'push']({ pathname: location.pathname, search: params.toString() })
    }, [history, location.pathname, location.search])

    useEffect(() => {
        if (query === filters.q) return undefined
        const timer = setTimeout(() => {
            updateUrl('q', query, true)
        }, 420)
        return () => clearTimeout(timer)
    }, [filters.q, query, updateUrl])

    const fail = useCallback((result, reason) => {
        const code = errorStatus(result)
        if (result?.error && ![401, 403].includes(code)) exposeStrapiError(result)
        trackEvent('competitive_intelligence_research_load_failed', { reason })
        setState(current => ({
            ...current,
            status: [401, 403].includes(code) ? 'denied' : code >= 500 || !result ? 'unavailable' : 'error',
            loadingMore: false,
        }))
    }, [])

    const load = useCallback(async ({ append = false, cursor = '' } = {}) => {
        const sequence = ++requestSequence.current
        setState(current => append ? { ...current, loadingMore: true } : {
            ...current, status: 'loading', items: [], loadingMore: false,
        })
        const periodDates = (() => {
            if (!filters.period) return { dateFrom: filters.dateFrom, dateTo: filters.dateTo }
            const now = new Date()
            if (/^\d{4}$/.test(filters.period)) return { dateFrom: `${filters.period}-01-01`, dateTo: `${filters.period}-12-31` }
            const days = filters.period === '30d' ? 30 : filters.period === '90d' ? 90 : 0
            if (!days) return { dateFrom: '', dateTo: '' }
            const from = new Date(now)
            from.setDate(from.getDate() - days)
            return { dateFrom: from.toISOString().slice(0, 10), dateTo: now.toISOString().slice(0, 10) }
        })()
        const result = await apiRequest(() => ReadCompetitiveIntelligenceResearch({
            q: filters.q,
            source: filters.source,
            competitor: filters.competitor,
            condition: filters.condition,
            status: filters.status,
            dateFrom: periodDates.dateFrom,
            dateTo: periodDates.dateTo,
            sort: filters.sort,
            cursor,
            limit: 24,
        }))
        if (sequence !== requestSequence.current) return
        if (!result || result?.error) {
            fail(result, `research_${errorStatus(result) || 'unavailable'}`)
            return
        }
        const data = result.data || {}
        const items = Array.isArray(data.items) ? data.items : []
        const hasQuery = Boolean(filters.q)
        const hasFilters = Boolean(filters.source || filters.competitor || filters.condition || filters.period || filters.status || filters.dateFrom || filters.dateTo)
        const status = append || items.length ? 'ready' : hasQuery ? 'emptySearch' : hasFilters ? 'emptyFilter' : 'emptyCorpus'
        trackEventOnce('competitive_intelligence_research_viewed', {
            competitive_section: 'research',
            result_count: Number(data.pagination?.total || 0),
            competitive_source: filters.source || 'all',
        }, `research:${location.search}`)
        if (filters.q) trackEvent(Number(data.pagination?.total || 0) > 0 ? 'COMPETITIVE_SEARCH_PERFORMED' : 'COMPETITIVE_SEARCH_NO_RESULTS', {
            competitive_section: 'research',
            query_length: filters.q.trim().length,
            result_count: Number(data.pagination?.total || 0),
            competitive_source: filters.source || undefined,
            competitor_id: filters.competitor || undefined,
        })
        setState(current => ({
            status,
            items: append ? [...current.items, ...items] : items,
            pagination: data.pagination || { total: 0, hasMore: false },
            facets: {
                sources: data.facets?.sources || [],
                statuses: data.facets?.statuses || [],
                competitors: data.facets?.competitors || [],
                conditions: data.facets?.conditions || [],
            },
            loadingMore: false,
        }))
    }, [fail, filters, location.search])

    useEffect(() => { load() }, [load])

    const onFilterChange = useCallback((name, value) => {
        trackEvent(value && value !== 'all' ? 'COMPETITIVE_FILTER_APPLIED' : 'COMPETITIVE_FILTER_REMOVED', {
            competitive_section: 'research', filter_type: name, filter_value: value || 'all',
        })
        updateUrl(name, value)
    }, [updateUrl])

    const onReset = useCallback(() => {
        trackEvent('COMPETITIVE_FILTER_REMOVED', {
            competitive_section: 'research', filter_type: 'all', filter_value: 'all',
        })
        setQuery('')
        history.push(location.pathname)
    }, [history, location.pathname])

    const onOpen = useCallback((item, index = 0) => {
        if (filters.q) trackEvent('COMPETITIVE_SEARCH_RESULT_CLICKED', {
            competitive_section: 'research', content_type: item.recordType || 'research', content_id: item.id,
            competitor_id: item.competitor, competitive_source: item.source, result_position: index + 1,
        })
        history.push(`/competitive-intelligence/research/${encodeURIComponent(item.id)}${location.search}`)
    }, [filters.q, history, location.search])

    const locale = LANGUAGE_LOCALES[language] || 'en-US'
    const formatCount = useMemo(() => new Intl.NumberFormat(locale).format, [locale])
    const formatDate = useCallback(value => {
        if (!value) return t('competitive_intelligence_freshness_unknown')
        const parsed = new Date(`${value}T00:00:00`)
        return Number.isNaN(parsed.getTime()) ? t('competitive_intelligence_freshness_unknown') : new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(parsed)
    }, [locale, t])
    const sourceOptions = useMemo(() => [
        { id: 'all', title: t('competitive_intelligence_research_source_all') },
        ...(state.facets.sources || []).map(item => ({ id: item.key, title: `${item.label} (${formatCount(item.count)})` })),
    ], [formatCount, state.facets.sources, t])
    const competitorOptions = useMemo(() => [
        { id: 'all', title: t('competitive_intelligence_research_competitor_all') },
        ...(state.facets.competitors || []).map(item => ({ id: item.key, title: `${item.label} (${formatCount(item.count)})` })),
    ], [formatCount, state.facets.competitors, t])
    const conditionOptions = useMemo(() => [
        { id: 'all', title: t('competitive_intelligence_research_condition_all') },
        ...(state.facets.conditions || []).map(item => ({ id: item.key, title: `${item.label} (${formatCount(item.count)})` })),
    ], [formatCount, state.facets.conditions, t])
    const periodOptions = useMemo(() => [
        { id: 'all', title: t('competitive_intelligence_period_all') },
        { id: '30d', title: t('competitive_intelligence_period_30d') },
        { id: '90d', title: t('competitive_intelligence_period_90d') },
        ...Array.from({ length: 7 }, (_, index) => {
            const year = new Date().getFullYear() - index
            return { id: String(year), title: String(year) }
        }),
    ], [t])

    return {
        ...state,
        competitorOptions,
        corpusTotal: (state.facets.sources || []).reduce((total, item) => total + Number(item.count || 0), 0),
        conditionOptions,
        filters,
        formatCount,
        formatDate,
        onFilterChange,
        onLoadMore: () => load({ append: true, cursor: state.pagination.nextCursor }),
        onOpen,
        onQueryChange: setQuery,
        onReset,
        query,
        retry: load,
        periodOptions,
        sourceOptions,
        t,
    }
}
